import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as React from "react";

import { ButtonStateEnum } from "@/enums";
import { generateValidClassNameHandler } from "@/lib/utils";
import { ButtonStateType } from "@/types";
import { buttonVariants } from "@/ui/button";

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({
    className,
    ...props
}, ref) => (
    <AlertDialogPrimitive.Overlay
        className={generateValidClassNameHandler(
            "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className,
        )}
        {...props}
        ref={ref}
    />
));

AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({
    className,
    ...props
}, ref) => (
    <AlertDialogPortal>
        <AlertDialogOverlay />
        <AlertDialogPrimitive.Content
            ref={ref}
            className={generateValidClassNameHandler(
                "fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] gap-2 md:gap-4 border border-gray-300 bg-card w-full max-w-lg p-3 md:p-6 rounded shadow-lg duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
                className,
            )}
            {...props}
        />
    </AlertDialogPortal>
));

AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={generateValidClassNameHandler(
            "flex flex-col space-y-2 text-center sm:text-left",
            className,
        )}
        {...props}
    />
);

AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={generateValidClassNameHandler(
            "flex sm:flex-row sm:justify-end sm:space-x-2 flex-row flex-nowrap items-center justify-end gap-2",
            className,
        )}
        {...props}
    />
);

AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({
    className,
    ...props
}, ref) => (
    <AlertDialogPrimitive.Title
        ref={ref}
        className={generateValidClassNameHandler(
            "text-sm md:text-lg font-semibold text-foreground",
            className,
        )}
        {...props}
    />
));

AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({
    className,
    ...props
}, ref) => (
    <AlertDialogPrimitive.Description
        ref={ref}
        className={generateValidClassNameHandler(
            "text-sm text-gray-500",
            className,
        )}
        {...props}
    />
));

AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

interface AlertDialogActionProps
    extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> {
    state?: ButtonStateType;
}

const AlertDialogAction = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Action>,
    AlertDialogActionProps
>(({
    className,
    state = ButtonStateEnum.DEFAULT,
    ...props
}, ref) => (
    <AlertDialogPrimitive.Action
        ref={ref}
        className={generateValidClassNameHandler(
            buttonVariants({ state }),
            "min-w-[76px]",
            className,
        )}
        {...props}
    />
));

AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
    React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({
    className,
    ...props
}, ref) => (
    <AlertDialogPrimitive.Cancel
        ref={ref}
        className={generateValidClassNameHandler(
            buttonVariants({ variant: "outline" }),
            "mt-0",
            className,
        )}
        {...props}
    />
));

AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogTitle,
    AlertDialogTrigger,
};
